import { CircleInfoIcon } from '@p2p/assets/svg';
import { Typography } from '@p2p/components';
import { getClientBankNameGenitive } from '@p2p/entities/qrInvoice/clientBankName';
import { isNoQrBank } from '@p2p/entities/qrInvoice/noQrBanks';
import { hasProviderQrImage } from '@p2p/entities/qrInvoice/providerQrImage';
import { TGetOrderResponse } from '@p2p/services';
import { getProviderPaymentCurrency } from '@p2p/shared/functions';
import { useDeviceDetect } from '@p2p/shared/hooks';

import {
  getInstructionInvoiceItemsDesk,
  getInstructionInvoiceItemsMobTabOnly,
  getInstructionInvoiceItemsNoQr,
  getProviderCurrencyName,
  INSTRUCTION_TITLE_DESK,
  INSTRUCTION_TITLE_MOB,
  instructionInvoiceSberNotes,
  PAY_AND_SAVE_RECEIPT_STEP,
} from './InstructionInvoice.constants';
import styles from './InstructionInvoice.module.scss';

type InstructionInvoiceProps = {
  order: TGetOrderResponse;
};

export const InstructionInvoice = ({ order }: InstructionInvoiceProps) => {
  const { isMobile } = useDeviceDetect();
  // P2P-3048: банк плательщика в текстах инструкции (только реквизит с qr_invoice).
  const bankName = getClientBankNameGenitive(order);
  // P2P-3043: у заявки с QR-картинкой провайдера оплата только по QR — инструкция про QR.
  const listItems =
    isNoQrBank(order) && !hasProviderQrImage(order)
      ? getInstructionInvoiceItemsNoQr(bankName)
      : isMobile
        ? getInstructionInvoiceItemsMobTabOnly(bankName)
        : getInstructionInvoiceItemsDesk(bankName);
  const title = isMobile ? INSTRUCTION_TITLE_MOB : INSTRUCTION_TITLE_DESK;
  // Примечания про Сбербанк — только для Сбера (P2P-2511, п.4).
  const isSber = order.client_bank_code?.toLowerCase() === 'sber';
  // P2P-2851 (03.11.2026): плательщик платит не в рублях (qr_amount + qr_currency_code,
  // MeridianPay) - в шаге оплаты выделяем, в какой валюте вводить сумму.
  const providerCurrency = getProviderPaymentCurrency(order);
  const providerCurrencyName = providerCurrency ? getProviderCurrencyName(providerCurrency) : null;

  return (
    <div className={styles.container}>
      <Typography variant="h3" color="heading">
        {title}
      </Typography>
      <ul className={styles.list}>
        {listItems.map((item, index) => (
          <Typography variant="text-m-medium" component="li" key={item} className={styles.listItem}>
            <span>{index + 1}.</span>
            {item === PAY_AND_SAVE_RECEIPT_STEP && providerCurrencyName ? (
              <span>
                Совершите оплату,{' '}
                <span className={styles.accent}>введя сумму {providerCurrencyName}</span> (она
                эквивалентна рублю), и сохраните чек.
              </span>
            ) : (
              <span>{item}</span>
            )}
          </Typography>
        ))}
      </ul>
      {isSber && (
        <div className={styles.notes}>
          {instructionInvoiceSberNotes.map((note) => (
            <div key={note} className={styles.note}>
              <CircleInfoIcon className={styles.noteIcon} />
              <Typography variant="text-m-medium">{note}</Typography>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
